<div class="iwb-v2" [class.iwb-fullscreen]="isFullscreen">
  <!-- Hidden warm-up: loads Monaco in the background so the state JSON tab and the
       fullscreen JSON editor open instantly instead of showing a blank pane. -->
  <ngx-monaco-editor
    *ngIf="!monacoWarm"
    class="iwb-monaco-warmup"
    [options]="monacoWarmupOptions"
    [ngModel]="''"
    (onInit)="onMonacoWarm()"></ngx-monaco-editor>
  <div class="iwb-toolbar">
    <div class="iwb-zoom" aria-label="Edit history">
      <button
        class="iwb-zbtn"
        type="button"
        title="Undo (⌘Z / Ctrl+Z)"
        aria-label="Undo"
        [disabled]="!canUndo"
        (click)="undo()">
        <i class="fa-solid fa-rotate-left"></i>
      </button>
      <button
        class="iwb-zbtn"
        type="button"
        title="Redo (⌘⇧Z / Ctrl+Shift+Z)"
        aria-label="Redo"
        [disabled]="!canRedo"
        (click)="redo()">
        <i class="fa-solid fa-rotate-right"></i>
      </button>
    </div>
    <span class="iwb-tspacer"></span>
    <span class="iwb-tgrouplbl">Tidy up</span>
    <div class="iwb-zoom">
      <button
        class="iwb-zbtn"
        [class.on]="tidyDir === 'vertical'"
        [disabled]="layoutBusy"
        title="Arrange top-down"
        (click)="tidyUp('vertical')">
        ↧ Vertical
      </button>
      <button
        class="iwb-zbtn"
        [class.on]="tidyDir === 'horizontal'"
        [disabled]="layoutBusy"
        title="Arrange left-to-right (fits wide workflows)"
        (click)="tidyUp('horizontal')">
        ↦ Horizontal
      </button>
    </div>
    <div class="iwb-zoom">
      <button class="iwb-zbtn" title="Zoom out" (click)="zoom('out')">−</button>
      <button
        class="iwb-zbtn iwb-zlevel"
        title="Reset zoom"
        (click)="zoom('reset')">
        {{ scale * 100 | number : '1.0-0' }}%
      </button>
      <button class="iwb-zbtn" title="Zoom in" (click)="zoom('in')">＋</button>
    </div>
    <div class="iwb-zoom">
      <button
        class="iwb-zbtn"
        type="button"
        title="Recenter on where the workflow starts"
        (click)="recenter()">
        ⌖ Recenter
      </button>
      <button
        class="iwb-zbtn"
        type="button"
        title="Zoom to fit the whole workflow in view"
        (click)="fitToView()">
        ⤢ Fit
      </button>
    </div>
    <button
      class="iwb-tbtn"
      [title]="isFullscreen ? 'Exit fullscreen' : 'Fullscreen'"
      (click)="toggleFullscreen()">
      <i
        class="fa-solid"
        [class.fa-compress]="isFullscreen"
        [class.fa-expand]="!isFullscreen"></i>
      {{ isFullscreen ? 'Exit' : 'Fullscreen' }}
    </button>
    <button
      class="iwb-tbtn"
      title="Edit the full workflow JSON in a fullscreen editor"
      (click)="openJsonEditor()">
      &#123;&#125; JSON
    </button>
  </div>
  <div class="wfb-warnings" *ngIf="warnings.length">
    <div
      class="wfb-warn"
      *ngFor="let w of warnings"
      [class.info]="w.severity === 'info'"
      [class.clickable]="w.stateCode || w.tid"
      [title]="
        w.stateCode || w.tid ? 'Click to jump to the affected state' : ''
      "
      (click)="focusWarning(w)">
      <span class="wico"
        ><i
          class="fa-solid"
          [class.fa-circle-info]="w.severity === 'info'"
          [class.fa-triangle-exclamation]="w.severity !== 'info'"></i
      ></span>
      <b>{{ w.kind }}</b
      >{{ w.stateCode ? ' · ' + w.stateCode : '' }} — {{ w.message }}
    </div>
  </div>
  <div class="body" style="min-height: 0; position: relative">
    <!-- empty canvas: every workflow starts at NEW with an APPLY event; the user only
         needs to choose where APPLY leads (the visual editor is otherwise blank).
         The NEW node + APPLY label reuse the real node/edge styling for coherence. -->
    <div class="iwb-empty" *ngIf="!model.states.length">
      <div class="iwb-empty-stack">
        <irembogov-iwb-state-node
          class="iwb-empty-node"
          [code]="'NEW'"
          [name]="'NEW'"
          [colour]="emptyStartColour"
          [endState]="null"
          [isTerminal]="false"
          [draggable]="false"
          (configure)="startFromScratch()"></irembogov-iwb-state-node>
        <span class="iwb-empty-conn"></span>
        <span class="evlabel iwb-empty-evlabel">
          <span class="evactor"
            ><i class="fa-solid fa-user"></i> ROLE_CITIZEN</span
          >
          <span class="evcode-row"
            ><span class="evarr">➜</span><span class="evcode">APPLY</span></span
          >
        </span>
        <span class="iwb-empty-conn iwb-empty-conn-arrow"></span>
        <button
          class="btn pri iwb-empty-choose"
          (click)="startFromScratch()"
          title="Every workflow starts at NEW with an APPLY event — pick the state it moves to">
          ＋ Choose where APPLY goes
        </button>
      </div>
    </div>
    <div
      class="canvas"
      #canvasEl
      style="width: 100%; height: 100%; overflow: auto"
      (wheel)="onWheel($event)"
      (pointerdown)="startPan($event)"
      (pointermove)="onPanMove($event)"
      (pointerup)="endPan($event)"
      (pointercancel)="endPan($event)">
      <div class="flow-wrap" #flowWrap>
        <div class="flow" #flowEl [style.transform]="'scale(' + scale + ')'">
          <!-- Draggable free canvas (the only mode) -->
          <div
            class="free-layer"
            [style.width.px]="freeSize.w"
            [style.height.px]="freeSize.h">
            <svg
              class="edges"
              [attr.width]="freeSize.w"
              [attr.height]="freeSize.h"
              [style.width.px]="freeSize.w"
              [style.height.px]="freeSize.h">
              <defs>
                <marker
                  id="iwb-arrow"
                  markerWidth="11"
                  markerHeight="11"
                  refX="8.5"
                  refY="4"
                  orient="auto"
                  markerUnits="userSpaceOnUse">
                  <path d="M0,0 L9,4 L0,8 Z" fill="context-stroke"></path>
                </marker>
              </defs>
              <g
                *ngFor="let e of freeEdges; trackBy: trackByEdge"
                class="edge"
                [attr.data-edge-id]="e.id"
                [attr.data-transition-id]="e.tid"
                [attr.data-source-key]="e.srcKey"
                [attr.data-target-key]="e.dstKey"
                [class.active]="isActiveEdge(e)"
                [class.sysevt]="e.isSystem"
                [class.unauth]="e.unauthorized"
                [class.iwb-dimmed]="isEdgeDimmed(e)"
                (click)="openEvent(e.tid); $event.stopPropagation()"
                (contextmenu)="onEventContext(e.tid, $event)">
                <!-- wide invisible hit area so the edge is easy to hover -->
                <path
                  [attr.d]="e.d"
                  fill="none"
                  stroke="transparent"
                  stroke-width="16"></path>
                <path
                  class="edgepath"
                  [attr.d]="e.d"
                  fill="none"
                  [attr.stroke]="
                    e.unauthorized
                      ? '#d92d20'
                      : e.isSystem
                      ? '#6938ef'
                      : '#475467'
                  "
                  stroke-width="2.5"
                  marker-end="url(#iwb-arrow)"
                  stroke-linecap="round"></path>
              </g>
            </svg>

            <!-- re-route handles: ABOVE the nodes so endpoints near a box edge stay grabbable -->
            <svg
              class="handles"
              [attr.width]="freeSize.w"
              [attr.height]="freeSize.h"
              [style.width.px]="freeSize.w"
              [style.height.px]="freeSize.h">
              <g *ngFor="let e of freeEdges; trackBy: trackByEdge">
                <circle
                  class="endpoint"
                  [attr.cx]="e.sx"
                  [attr.cy]="e.sy"
                  r="5.5"
                  (pointerdown)="onEndpointDown(e, 'src', $event)"
                  title="drag to re-route"></circle>
                <circle
                  class="endpoint"
                  [attr.cx]="e.ex"
                  [attr.cy]="e.ey"
                  r="5.5"
                  (pointerdown)="onEndpointDown(e, 'dst', $event)"
                  title="drag to re-route"></circle>
              </g>
            </svg>

            <!-- event labels as design-system pills (clickable → event config) -->
            <div class="edge-labels">
              <span
                class="evlabel"
                *ngFor="let e of freeEdges; trackBy: trackByEdge"
                [attr.data-edge-id]="e.id"
                [attr.data-transition-id]="e.tid"
                [class.active]="isActiveEdge(e)"
                [class.sysevt]="e.isSystem"
                [class.unauth]="e.unauthorized"
                [class.iwb-dimmed]="isEdgeDimmed(e)"
                [style.left.px]="e.mx"
                [style.top.px]="e.my"
                (click)="openEvent(e.tid); $event.stopPropagation()"
                (contextmenu)="onEventContext(e.tid, $event)"
                [class.iwb-evlabel-plain]="e.branch"
                [title]="
                  (e.branch
                    ? 'Branch outcome — click to edit the condition'
                    : e.unauthorized
                    ? 'No authorised role — click to fix'
                    : 'Click to configure event') +
                  ' · right-click or ⌘C to copy'
                ">
                <ng-container *ngIf="!e.branch">
                  <span class="evactor"
                    ><i
                      class="fa-solid"
                      [class.fa-gear]="e.isSystem"
                      [class.fa-user]="!e.isSystem"></i>
                    {{ e.actor }}</span
                  >
                  <span class="evcode-row"
                    ><span class="evarr">➜</span
                    ><span class="evcode">{{ e.label }}</span
                    ><span
                      class="evoutcome"
                      *ngIf="e.outcome"
                      [title]="
                        e.outcome === '②'
                          ? 'End State Two (condition met)'
                          : 'End State One (default)'
                      "
                      >{{ outcomeLabel(e) }}</span
                    ><span class="evwarn" *ngIf="e.unauthorized"
                      ><i class="fa-solid fa-triangle-exclamation"></i></span
                  ></span>
                </ng-container>
                <span
                  *ngIf="e.branch"
                  class="branchlabel"
                  [class.true]="e.branch === 'true'"
                  [class.false]="e.branch === 'false'"
                  >{{ outcomeLabel(e) }}</span
                >
              </span>
            </div>

            <!-- decision diamonds: ◆ IF node for any transition with a guard (endStateCondition) -->
            <div class="edge-labels iwb-decisions">
              <div
                class="iwb-decision"
                *ngFor="let d of decisions; trackBy: trackByDecision"
                [attr.data-code]="'cond:' + d.tid"
                [class.active]="
                  (inspector?.mode === 'condition' ||
                    inspector?.mode === 'event') &&
                  inspector?.tid === d.tid
                "
                [style.left.px]="d.x"
                [style.top.px]="d.y">
                <irembogov-iwb-condition-node
                  [transition]="d.t"
                  (configure)="openEvent(d.tid)"></irembogov-iwb-condition-node>
              </div>
            </div>

            <div
              class="free-node"
              *ngFor="let s of visualGraph.nodes; trackBy: trackByVisualNode"
              [attr.data-code]="s.key"
              [attr.data-node-key]="s.key"
              [attr.data-state-code]="s.stateCode"
              [attr.data-transition-id]="s.ref?.tid || null"
              [attr.data-outcome]="s.ref?.which || null"
              [class.active]="s.key === activeStateKey"
              [class.iwb-dimmed]="isStateDimmed(s.key)"
              [class.iwb-move-target]="s.key === moveDropKey"
              [class.iwb-focus-flash]="s.stateCode === focusedStateCode"
              [style.left.px]="renderX(s.key)"
              [style.top.px]="renderY(s.key)"
              [attr.title]="
                s.ref
                  ? 'Right-click or ⌘C to copy the event that lands here · drag an edge endpoint to move it'
                  : null
              "
              (pointerdown)="onNodePointerDown(s, $event)"
              (contextmenu)="onStateContext(s, $event)">
              <!-- connection points (visible on hover): drag from one to add an event -->
              <span
                class="iwb-anchor"
                *ngFor="let a of anchorNames"
                [ngStyle]="anchorStyle(s.key, a)"
                (pointerdown)="onAnchorAddEvent(s.stateCode, a, $event)"
                (click)="onAnchorAddEvent(s.stateCode, a, $event)"
                title="Drag from here to add an event"></span>
              <irembogov-iwb-state-node
                [nodeKey]="s.key"
                [code]="s.stateCode"
                [name]="stateName(s.stateCode)"
                [colour]="colour(s.stateCode)"
                [endState]="endStateForNode(s)"
                [isTerminal]="isTerminal(s.stateCode)"
                [isDeadEnd]="isDeadEnd(s.stateCode)"
                [occurrenceIndex]="s.occurrenceIndex"
                [occurrenceCount]="s.occurrenceCount"
                [pathTitle]="incomingPathFor(s)"
                [draggable]="true"
                (configure)="openState(refForNode(s))"
                (configureAction)="openAction(refForNode(s), $event)"
                (chipContext)="onActionContext($event, refForNode(s))"
                (addAction)="openState(refForNode(s))"
                (chipDragStart)="onChipDragStart($event, s.key)"
                (chipDragEnd)="onChipDragEnd()"
                (chipDropped)="
                  onChipDropTo(refForNode(s), s.key)
                "></irembogov-iwb-state-node>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <irembogov-iwb-inspector
    *ngIf="inspector"
    [target]="inspector"
    [organizationId]="organizationId"
    [formFields]="formFields"
    [apiStates]="apiStates"
    [sharedStateOccurrence]="!!activeStateKey?.startsWith('outcome:')"
    (actionEdit)="onInspectorActionEdit($event)"
    (stateEditRequest)="onInspectorStateEdit()"
    (eventCopied)="onEventCopied($event)"
    (actionCopied)="onActionCopied($event)"
    (deleteEventRequest)="deleteEvent($event)"
    (deleteStateRequest)="deleteStateNode($event)"
    (deleteActionRequest)="deleteAction($event)"
    (closed)="closeInspector()"></irembogov-iwb-inspector>

  <div class="iwb-copy-notice" *ngIf="copyNotice">{{ copyNotice }}</div>

  <!-- right-click context menu -->
  <ng-container *ngIf="ctxMenu">
    <div
      class="iwb-ctx-backdrop"
      (click)="closeCtxMenu()"
      (contextmenu)="$event.preventDefault(); closeCtxMenu()"></div>
    <div
      class="iwb-ctx-menu"
      [style.left.px]="ctxMenu.x"
      [style.top.px]="ctxMenu.y">
      <button
        class="iwb-ctx-item"
        *ngFor="let it of ctxMenu.items"
        (click)="runCtx(it)">
        {{ it.label }}
      </button>
    </div>
  </ng-container>

  <!-- delete confirmation (warns what will be removed before any state/event is deleted) -->
  <ng-container *ngIf="confirmDelete as cd">
    <div class="iwb-confirm-backdrop" (click)="confirmDeleteCancel()"></div>
    <div class="iwb-confirm" role="alertdialog" aria-modal="true">
      <div class="iwb-confirm-title">{{ cd.heading }}</div>
      <div class="iwb-confirm-body">
        <p *ngIf="cd.kind === 'state'">
          This permanently removes the state and every event into or out of it.
        </p>
        <p *ngIf="cd.kind === 'event'">
          This permanently removes the event{{
            cd.states.length ? ' and the state(s) it leads to' : ''
          }}.
        </p>
        <p *ngIf="cd.kind === 'action'">
          This permanently removes the action from this state. Other actions and
          the workflow state stay unchanged.
        </p>
        <div class="iwb-confirm-list" *ngIf="cd.kind === 'action'">
          <span class="iwb-confirm-lbl">Action removed</span>
          <span class="iwb-confirm-chips">
            <span class="iwb-confirm-chip">{{
              cd.actionType || 'Action'
            }}</span>
          </span>
        </div>
        <div class="iwb-confirm-list" *ngIf="cd.events.length">
          <span class="iwb-confirm-lbl">Event(s) removed</span>
          <span class="iwb-confirm-chips">
            <span class="iwb-confirm-chip" *ngFor="let e of cd.events">{{
              e
            }}</span>
          </span>
        </div>
        <div class="iwb-confirm-list" *ngIf="cd.states.length">
          <span class="iwb-confirm-lbl">State(s) removed</span>
          <span class="iwb-confirm-chips">
            <span class="iwb-confirm-chip" *ngFor="let s of cd.states">{{
              s
            }}</span>
          </span>
        </div>
        <p
          class="iwb-confirm-none"
          *ngIf="cd.kind === 'event' && !cd.states.length">
          No state is left orphaned — only the event is removed.
        </p>
      </div>
      <div class="iwb-confirm-actions">
        <button class="btn" (click)="confirmDeleteCancel()">Cancel</button>
        <button class="btn iwb-danger-solid" (click)="confirmDeleteRun()">
          Delete
        </button>
      </div>
    </div>
  </ng-container>

  <!-- move confirmation (re-anchoring an event's arrow onto another state) -->
  <ng-container *ngIf="confirmMove as cm">
    <div class="iwb-confirm-backdrop" (click)="confirmMoveCancel()"></div>
    <div class="iwb-confirm" role="alertdialog" aria-modal="true">
      <div class="iwb-confirm-title">{{ cm.heading }}</div>
      <div class="iwb-confirm-body">
        <p *ngIf="cm.end === 'dst'">
          The event now leads to
          <strong>{{ cm.toCode }}</strong> instead of
          <strong>{{ cm.fromCode }}</strong
          >.
        </p>
        <p *ngIf="cm.end === 'src'">
          The event now leaves from
          <strong>{{ cm.toCode }}</strong> instead of
          <strong>{{ cm.fromCode }}</strong
          >. The event and its actions travel with it.
        </p>
        <div class="iwb-confirm-list" *ngIf="cm.outcomeActions.length">
          <label class="iwb-confirm-check">
            <input type="checkbox" [(ngModel)]="cm.carryActions" />
            Carry this outcome’s action(s) to
            <strong>{{ cm.toCode }}</strong>
          </label>
          <span class="iwb-confirm-chips">
            <span
              class="iwb-confirm-chip"
              *ngFor="let a of cm.outcomeActions"
              >{{ a }}</span
            >
          </span>
          <span class="iwb-confirm-hint" *ngIf="!cm.carryActions">
            These actions will be dropped —
            <strong>{{ cm.toCode }}</strong> is reached with no actions.
          </span>
        </div>
        <div class="iwb-confirm-list" *ngIf="cm.disappears.length">
          <span class="iwb-confirm-lbl">State(s) that disappear</span>
          <span class="iwb-confirm-chips">
            <span class="iwb-confirm-chip" *ngFor="let s of cm.disappears">{{
              s
            }}</span>
          </span>
        </div>
        <div class="iwb-confirm-cascade" *ngIf="cm.cascade as cas">
          <p>
            <strong>{{ cas.code }}</strong> is no longer reached by any event,
            but still has {{ cas.events.length }} onward event(s):
            <span class="iwb-confirm-chips">
              <span class="iwb-confirm-chip" *ngFor="let e of cas.events">{{
                e
              }}</span>
            </span>
          </p>
          <label class="iwb-confirm-check">
            <input type="checkbox" [(ngModel)]="cm.cascadeRepoint" />
            Also re-point those onward events to start from
            <strong>{{ cm.toCode }}</strong> (then edit).
          </label>
        </div>
        <p
          class="iwb-confirm-none"
          *ngIf="
            !cm.outcomeActions.length && !cm.disappears.length && !cm.cascade
          ">
          Nothing else changes — only the arrow moves.
        </p>
      </div>
      <div class="iwb-confirm-actions">
        <button class="btn" (click)="confirmMoveCancel()">Cancel</button>
        <button class="btn pri" (click)="confirmMoveRun()">Move</button>
      </div>
    </div>
  </ng-container>

  <!-- move blocked: the target already owns this event, so the move would override it -->
  <ng-container *ngIf="blockedMove as bm">
    <div class="iwb-confirm-backdrop" (click)="dismissBlockedMove()"></div>
    <div class="iwb-confirm" role="alertdialog" aria-modal="true">
      <div class="iwb-confirm-title">{{ bm.heading }}</div>
      <div class="iwb-confirm-body">
        <p>{{ bm.message }}</p>
        <div class="iwb-confirm-list" *ngIf="bm.event">
          <span class="iwb-confirm-lbl">Existing event</span>
          <span class="iwb-confirm-chips">
            <span class="iwb-confirm-chip">{{ bm.event }}</span>
          </span>
        </div>
        <p class="iwb-confirm-none">
          Nothing was changed — <strong>{{ bm.toLabel }}</strong> keeps all its
          actions and configuration.
        </p>
      </div>
      <div class="iwb-confirm-actions">
        <button class="btn pri" (click)="dismissBlockedMove()">Got it</button>
      </div>
    </div>
  </ng-container>

  <ng-container *ngIf="composerOpen">
    <div class="backdrop" (click)="closeComposer()"></div>
    <div
      style="
        position: fixed;
        z-index: 70;
        width: 330px;
        overflow-y: auto;
        border-radius: 12px;
      "
      [style.maxHeight.px]="composerMaxH"
      [style.left.px]="composerPos.x"
      [style.top.px]="composerPos.y">
      <irembogov-iwb-add-event
        [fromState]="composerFrom || ''"
        [lockEvent]="composerFirst ? 'APPLY' : ''"
        [paste]="composerPaste"
        [states]="model.states"
        [apiStates]="apiStates"
        (done)="closeComposer()"
        (cancelled)="closeComposer()"></irembogov-iwb-add-event>
    </div>
  </ng-container>

  <!-- Reused wizard / v1 managers (whole-workflow), opened from the toolbar -->
  <div class="iwb-drawer-host" *ngIf="manager">
    <div class="backdrop" (click)="closeManager()"></div>
    <div class="drawer" style="width: 600px">
      <div class="dhead">
        <div class="dtitle" [ngSwitch]="manager">
          <b *ngSwitchCase="'notification'">🔔 Notifications</b>
          <b *ngSwitchCase="'office'">🏢 Office assignment</b>
          <b *ngSwitchCase="'receipt'">🧾 Receipt name</b>
        </div>
        <button class="dx" (click)="closeManager()">✕</button>
      </div>
      <div class="dbodywrap">
        <irembogov-notification-configuration
          *ngIf="manager === 'notification'"
          [changeRequestJsonWorkflowData]="$any(json)"
          [formFields]="formFields"
          (workflowUpdated)="
            onManagerUpdate($event)
          "></irembogov-notification-configuration>

        <irembogov-office-assignment-config
          *ngIf="manager === 'office'"
          [changeRequestJsonWorkflowData]="$any(json)"
          [organizationId]="organizationId"
          (workflowUpdated)="
            onManagerUpdate($event)
          "></irembogov-office-assignment-config>

        <irembogov-configuration-receipt-name
          *ngIf="manager === 'receipt'"
          [changeRequestJsonWorkflowData]="$any(json)"
          [formFields]="formFields"
          (workflowUpdated)="
            onManagerUpdate($event)
          "></irembogov-configuration-receipt-name>
      </div>
    </div>
  </div>

  <irembogov-iwb-fullscreen-json-editor
    *ngIf="showJsonEditor"
    [json]="json"
    [validate]="validateWorkflowJson"
    (save)="onJsonEditorSave($event)"
    (closed)="showJsonEditor = false"></irembogov-iwb-fullscreen-json-editor>
</div>
